const configElement = document.getElementById('NmsCommerceConfig');
const config = configElement ? JSON.parse(configElement.textContent) : {};
const strings = config.strings || {};
const t = (key, values = {}) => {
  let result = strings[key] || key;
  for (const [name, value] of Object.entries(values)) result = result.replaceAll(`{{ ${name} }}`, String(value)).replaceAll(`__${name.toUpperCase()}__`, String(value));
  return result;
};
const money = (cents, currency = config.currency || 'USD') => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
const initialized = new WeakSet();
let cartBusy = false;

function status(root, text, error = false) {
  const node = root.querySelector('[data-nms-status]');
  if (!node) return;
  node.hidden = !text;
  node.textContent = text;
  node.classList.toggle('is-error', error);
}

async function addToCart(root, payload, form = null) {
  if (cartBusy) return status(root, t('updating'));
  cartBusy = true;
  const productRoot = form?.closest('[data-nms-product]');
  const controls = [...(productRoot || root).querySelectorAll('button, select, input:not([type="hidden"])')];
  const before = controls.map(element => ({ element, disabled: element.disabled }));
  controls.forEach(element => element.disabled = true);
  root.setAttribute('aria-busy', 'true');
  status(root, t('adding'));
  let cartConfirmed = false;
  try {
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(element => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const isForm = payload instanceof FormData;
    if (isForm) {
      if (sections.length) payload.set('sections', sections.join(','));
      payload.set('sections_url', location.pathname);
    } else {
      if (sections.length) payload.sections = sections.join(',');
      payload.sections_url = location.pathname;
    }
    const response = await fetch(config.addUrl, { method: 'POST', headers: isForm ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' }, body: isForm ? payload : JSON.stringify(payload) });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || t('add_error'));
    cartConfirmed = true;
    status(root, t('added'));
    const quantity = isForm ? Number(payload.get('quantity') || 1) : payload.items.reduce((sum, item) => sum + item.quantity, 0);
    const id = isForm ? String(payload.get('id')) : String(payload.items[0].id);
    if (window.AmsCartDrawer && window.AmsCartDrawer.enabled()) {
      await window.AmsCartDrawer.open();
      return;
    }
    const drawer = document.querySelector('cart-drawer-component');
    if (config.cartType !== 'drawer' || !drawer || !result.sections || sections.some(section => !result.sections[section])) {
      location.assign(config.cartUrl);
      return;
    }
    try {
      const { CartAddEvent } = await import('@theme/events');
      (form || root).dispatchEvent(new CartAddEvent(result, id, { source: 'product-form-component', itemCount: quantity, productId: productRoot?.dataset.productId, variantId: id, sections: result.sections }));
      if (typeof drawer.open === 'function') drawer.open();
      else location.assign(config.cartUrl);
    } catch {
      location.assign(config.cartUrl);
    }
  } catch (error) {
    status(root, cartConfirmed ? t('added') : (error instanceof Error ? error.message : t('add_error')), !cartConfirmed);
    if (cartConfirmed) location.assign(config.cartUrl);
  } finally {
    cartBusy = false;
    root.removeAttribute('aria-busy');
    before.forEach(({ element, disabled }) => element.disabled = disabled);
  }
}

document.addEventListener('submit', event => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-nms-quick-form], [data-nms-product-form]')) return;
  if (event.submitter && event.submitter.name !== 'add') return;
  if (!form.reportValidity()) return;
  event.preventDefault();
  const data = new FormData(form);
  if (!data.get('id')) return status(form, t('unavailable'), true);
  addToCart(form, data, form);
});

function initializeProduct(root) {
  if (initialized.has(root)) return;
  initialized.add(root);
  const data = JSON.parse(root.querySelector('[data-nms-product-data]').textContent);
  const variants = data.variants;
  const form = root.querySelector('[data-nms-product-form]');
  const select = root.querySelector('[data-nms-variant-select]');
  const quantity = form.querySelector('[name="quantity"]');
  const mainImage = root.querySelector('[data-nms-main-image]');
  const zoomDialog = root.querySelector('[data-nms-zoom-dialog]');
  const zoomImage = root.querySelector('[data-nms-zoom-image]');
  const planSelect = form.querySelector('[data-nms-selling-plan]');
  const purchaseOptions = form.querySelector('[data-nms-purchase-options]');
  const frequencyWrap = form.querySelector('[data-nms-frequency-wrap]');
  const purchaseButtons = [...form.querySelectorAll('[data-nms-purchase-mode]')];
  const oneTimePrice = form.querySelector('[data-nms-one-time-price]');
  const subscribePrice = form.querySelector('[data-nms-subscribe-price]');
  const subscribeBadge = form.querySelector('[data-nms-subscribe-badge]');
  let current = variants.find(variant => String(variant.id) === String(data.selected)) || variants[0];
  const setImage = (src, alt, srcset, imageId) => {
    if (!mainImage || !src) return;
    mainImage.src = src;
    if (srcset) mainImage.srcset = srcset;
    else mainImage.removeAttribute('srcset');
    mainImage.alt = alt || '';
    if (zoomImage) { zoomImage.src = src; zoomImage.alt = alt || ''; }
    root.querySelectorAll('[data-nms-thumb]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.imageId === String(imageId))));
  };
  const srcsetFor = src => {
    if (!src) return '';
    const url = new URL(src, location.origin);
    return [360, 540, 720, 1000, 1400].map(width => { url.searchParams.set('width', String(width)); return `${url.href} ${width}w`; }).join(', ');
  };
  const renderPurchaseOptions = () => {
    if (!planSelect || !purchaseOptions) return;
    const selectedPlan = current.plans.find(plan => String(plan.id) === String(planSelect.value));
    const firstPlan = selectedPlan || current.plans[0] || null;
    const isSubscription = Boolean(planSelect.value && selectedPlan);
    purchaseButtons.forEach(button => button.setAttribute('aria-pressed', String(
      button.dataset.nmsPurchaseMode === (isSubscription ? 'subscribe' : 'one-time')
    )));
    if (frequencyWrap) frequencyWrap.hidden = !isSubscription;
    if (oneTimePrice) oneTimePrice.textContent = money(current.price, root.dataset.currency);
    if (subscribePrice) subscribePrice.textContent = money((firstPlan || { price: current.price }).price, root.dataset.currency);
    if (subscribeBadge) {
      const discount = firstPlan && current.price > 0 ? Math.max(0, Math.round((1 - firstPlan.price / current.price) * 100)) : 0;
      subscribeBadge.textContent = discount > 0 ? `Save ${discount}%` : 'Subscribe';
    }
  };
  const renderPrice = () => {
    const allocation = current.plans.find(plan => String(plan.id) === String(planSelect?.value));
    const price = allocation ? allocation.price : current.price;
    root.querySelector('[data-nms-price]').textContent = allocation ? money(price, root.dataset.currency) : current.formatted.replace(/<[^>]*>/g, '');
    root.querySelector('[data-nms-sticky-price]').textContent = money(price, root.dataset.currency);
    const compare = root.querySelector('[data-nms-compare]');
    compare.hidden = !(current.compare > price);
    compare.textContent = money(current.compare, root.dataset.currency);
    const unit = root.querySelector('[data-nms-unit]');
    unit.hidden = current.count <= 0;
    unit.textContent = current.count > 0 ? `${money(price / current.count, root.dataset.currency)} ${t('per_meal')} · ${current.count} ${t('meals')}` : '';
    renderPurchaseOptions();
  };
  const update = (id, changeUrl = true) => {
    const next = variants.find(variant => String(variant.id) === String(id));
    if (!next) return;
    current = next;
    if (select) select.value = String(next.id);
    root.querySelectorAll('[data-nms-variant]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.nmsVariant === String(next.id))));
    const availability = root.querySelector('[data-nms-availability]');
    availability.textContent = next.available ? t('in_stock') : t('sold_out');
    availability.classList.toggle('is-sold-out', !next.available);
    root.querySelector('[data-nms-add]').disabled = !next.available;
    root.querySelector('[data-nms-add] span').textContent = next.available ? t('add') : t('sold_out');
    const stickyAdd = root.querySelector('[data-nms-sticky-add]');
    stickyAdd.disabled = !next.available;
    stickyAdd.textContent = next.available ? t('add') : t('sold_out');
    const selection = root.querySelector('[data-nms-selection]');
    selection.textContent = next.title === 'Default Title' ? '' : next.title;
    root.querySelector('[data-nms-sticky-title]').textContent = next.title === 'Default Title' ? root.querySelector('h1').textContent : next.title;
    const sku = root.querySelector('[data-nms-sku]');
    sku.hidden = !next.sku;
    sku.textContent = `SKU: ${next.sku || ''}`;
    const caption = root.querySelector('[data-nms-image-caption]');
    if (caption) caption.textContent = next.title;
    quantity.min = String(next.min || 1);
    quantity.step = String(next.step || 1);
    if (next.max != null) quantity.max = String(next.max);
    else quantity.removeAttribute('max');
    let value = Math.max(next.min || 1, Number(quantity.value) || 1);
    value = (next.min || 1) + Math.ceil((value - (next.min || 1)) / (next.step || 1)) * (next.step || 1);
    quantity.value = String(next.max == null ? value : Math.min(value, next.max));
    if (planSelect) {
      const previous = planSelect.value;
      const wasSubscribed = Boolean(previous);
      planSelect.replaceChildren();
      if (!data.requiresPlan) planSelect.add(new Option(t('one_time'), ''));
      next.plans.forEach(plan => planSelect.add(new Option(`${plan.name} — ${money(plan.price, root.dataset.currency)}`, String(plan.id))));
      if ([...planSelect.options].some(option => option.value === previous)) planSelect.value = previous;
      else if (wasSubscribed && next.plans.length) planSelect.value = String(next.plans[0].id);
      else if (data.requiresPlan && next.plans.length) planSelect.value = String(next.plans[0].id);
      if (purchaseOptions) purchaseOptions.hidden = next.plans.length === 0;
      if (data.requiresPlan && !next.plans.length) { root.querySelector('[data-nms-add]').disabled = true; stickyAdd.disabled = true; }
    }
    renderPrice();
    setImage(next.src, next.title === 'Default Title' ? root.querySelector('h1').textContent : next.title, srcsetFor(next.src), next.imageId);
    if (changeUrl) {
      const url = new URL(location.href);
      url.searchParams.set('variant', String(next.id));
      history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    form.dispatchEvent(new Event('change', { bubbles: true }));
    status(form, '');
  };
  select?.addEventListener('change', () => update(select.value));
  root.querySelectorAll('[data-nms-variant]').forEach(button => button.addEventListener('click', () => update(button.dataset.nmsVariant)));
  const variantGrid = root.querySelector('[data-nms-variant-grid]');
  if (variantGrid && select) { variantGrid.hidden = false; select.hidden = true; }
  if (root.dataset.productHandle === 'mre-drink-chips-combo' && select) {
    const pairs = variants.map(variant => { const parts = variant.title.split(' + '); return { ...variant, drink: parts[1]?.trim(), chips: parts[2]?.trim() }; });
    if (pairs.every(pair => pair.drink && pair.chips)) {
      const drink = root.querySelector('[data-nms-combo-drink]');
      const chips = root.querySelector('[data-nms-combo-chips]');
      [...new Set(pairs.map(pair => pair.drink))].forEach(name => drink.add(new Option(name, name)));
      const initial = pairs.find(pair => pair.id === current.id);
      drink.value = initial.drink;
      const updateChips = preferred => {
        const options = [...new Set(pairs.filter(pair => pair.drink === drink.value).map(pair => pair.chips))];
        chips.replaceChildren(...options.map(name => new Option(name.replace(/^Better Made /, ''), name)));
        if (options.includes(preferred)) chips.value = preferred;
      };
      updateChips(initial.chips);
      drink.addEventListener('change', () => { updateChips(chips.value); const next = pairs.find(pair => pair.drink === drink.value && pair.chips === chips.value); if (next) update(next.id); });
      chips.addEventListener('change', () => { const next = pairs.find(pair => pair.drink === drink.value && pair.chips === chips.value); if (next) update(next.id); });
      root.querySelector('[data-nms-combo-controls]').hidden = false;
      select.hidden = true;
    }
  }
  const adjustQuantity = delta => {
    const min = Number(quantity.min) || 1, step = Number(quantity.step) || 1, max = quantity.max ? Number(quantity.max) : Infinity;
    const old = Number(quantity.value) || min;
    quantity.value = String(Math.min(max, Math.max(min, min + Math.round((old - min) / step) * step + delta * step)));
    quantity.dispatchEvent(new Event('change', { bubbles: true }));
  };
  root.querySelector('[data-nms-minus]').addEventListener('click', () => adjustQuantity(-1));
  root.querySelector('[data-nms-plus]').addEventListener('click', () => adjustQuantity(1));
  purchaseButtons.forEach(button => button.addEventListener('click', () => {
    if (!planSelect) return;
    if (button.dataset.nmsPurchaseMode === 'one-time') {
      planSelect.value = '';
    } else if (current.plans.length) {
      const existing = current.plans.find(plan => String(plan.id) === String(planSelect.value));
      planSelect.value = String((existing || current.plans[0]).id);
    }
    planSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }));
  planSelect?.addEventListener('change', renderPrice);
  root.querySelectorAll('[data-nms-thumb]').forEach(button => button.addEventListener('click', () => setImage(button.dataset.src, button.dataset.alt, button.dataset.srcset, button.dataset.imageId)));
  const zoom = root.querySelector('[data-nms-zoom]');
  if (zoom && zoomDialog?.showModal) {
    zoom.hidden = false;
    zoom.addEventListener('click', () => zoomDialog.showModal());
    root.querySelector('[data-nms-zoom-close]').addEventListener('click', () => zoomDialog.close());
    zoomDialog.addEventListener('click', event => { if (event.target === zoomDialog) { const rect = zoomDialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) zoomDialog.close(); } });
  }
  const sticky = root.querySelector('[data-nms-sticky]');
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => { const entry = entries[0]; sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0; document.body.classList.toggle('nms-sticky-visible', !sticky.hidden); }, { threshold: 0 });
    observer.observe(root.querySelector('[data-nms-add]'));
    document.addEventListener('shopify:section:unload', event => { if (event.target.contains(root)) { observer.disconnect(); document.body.classList.remove('nms-sticky-visible'); } }, { once: true });
  }
  update(current.id, false);
}

function findSupply(variants, target) {
  const options = variants.filter(variant => variant.available && variant.count > 0 && variant.price >= 0 && variant.min === 1 && variant.step === 1);
  if (!options.length) return null;
  const limit = target + Math.max(...options.map(variant => variant.count)) - 1;
  const costs = Array(limit + 1).fill(Infinity), plans = Array(limit + 1).fill(null);
  costs[0] = 0; plans[0] = [];
  for (let total = 1; total <= limit; total++) {
    options.forEach(variant => {
      if (total < variant.count || !Number.isFinite(costs[total - variant.count])) return;
      const prior = plans[total - variant.count];
      const used = prior.filter(id => id === variant.id).length;
      if (variant.max != null && used >= variant.max) return;
      const price = costs[total - variant.count] + variant.price;
      if (price < costs[total]) { costs[total] = price; plans[total] = [...prior, variant.id]; }
    });
  }
  let best = -1;
  for (let total = target; total <= limit; total++) if (Number.isFinite(costs[total]) && (best < 0 || costs[total] < costs[best])) best = total;
  if (best < 0) return null;
  const counts = new Map();
  plans[best].forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
  return { count: best, price: costs[best], items: [...counts].map(([id, quantity]) => ({ id, quantity, variant: options.find(variant => variant.id === id) })) };
}

function initializePlanner(root) {
  if (initialized.has(root)) return;
  initialized.add(root);
  const variants = JSON.parse(root.querySelector('[data-calc-variants]').textContent);
  const form = root.querySelector('[data-nms-calc-form]');
  const result = root.querySelector('[data-calc-result]');
  const add = root.querySelector('[data-calc-add]');
  const bulk = root.querySelector('[data-calc-bulk]');
  let plan = null;
  function calculate() {
    if (!form.reportValidity()) return;
    const inputs = [...form.querySelectorAll('input')].map(input => Number(input.value));
    if (inputs.some(value => !Number.isInteger(value) || value < 1)) return;
    const target = inputs.reduce((a, b) => a * b, 1);
    result.replaceChildren(); status(root, '');
    const heading = document.createElement('strong'); heading.textContent = t('target', { count: target.toLocaleString() }); result.append(heading);
    add.hidden = true; bulk.hidden = true; plan = null;
    if (target > 576) { const p = document.createElement('p'); p.textContent = t('bulk_message'); result.append(p); bulk.hidden = false; return; }
    plan = findSupply(variants, target);
    if (!plan) { const p = document.createElement('p'); p.textContent = t('empty'); result.append(p); bulk.hidden = false; return; }
    const list = document.createElement('ul');
    plan.items.forEach(item => { const li = document.createElement('li'); li.textContent = `${item.quantity} × ${item.variant.title}`; list.append(li); });
    const included = document.createElement('p'); included.textContent = t('included', { count: plan.count });
    const price = document.createElement('p'); price.className = 'nms-plan-total'; price.textContent = money(plan.price, root.dataset.currency);
    const note = document.createElement('small'); note.textContent = t('shipping'); price.append(note);
    result.append(list, included, price); add.hidden = false;
  }
  form.addEventListener('submit', event => { event.preventDefault(); calculate(); });
  form.addEventListener('input', () => { plan = null; add.hidden = true; bulk.hidden = true; });
  add.addEventListener('click', () => { if (plan) addToCart(root, { items: plan.items.map(({ id, quantity }) => ({ id, quantity })) }); });
  calculate();
}

function initialize(scope = document) {
  scope.querySelectorAll('[data-nms-product]').forEach(initializeProduct);
  scope.querySelectorAll('[data-nms-planner]').forEach(initializePlanner);
  scope.querySelectorAll('[data-nms-filter-section]').forEach(root => {
    if (initialized.has(root)) return;
    initialized.add(root);
    const controls = root.querySelector('[data-nms-filter-controls]');
    controls.hidden = false;
    controls.querySelectorAll('[data-filter]').forEach(button => button.addEventListener('click', () => {
      controls.querySelectorAll('[data-filter]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      root.querySelectorAll('[data-nms-filter-items] > [data-group]').forEach(card => card.hidden = button.dataset.filter !== 'all' && card.dataset.group !== button.dataset.filter);
    }));
  });
}
initialize();
document.addEventListener('shopify:section:load', event => initialize(event.target));
